'use client';

import * as React from 'react';
import { Moon, Sun } from 'lucide-react';
import { MockChatClient, useChat, type ChatClient } from '@langbridge-builder/chat-ui';
import { A2uiPanel } from '@langbridge-builder/chat-ui/a2ui';
import {
  attachCapability,
  DEFAULT_CONFIG,
  getAgentIo,
  makeAguiClient,
  WebappSurface,
  type AgentIo,
} from '@langbridge-builder/webapp';
import type { PublishedBotConfig } from './published-config';

const MOCK_REPLY = '이 챗봇에 에이전트가 연결되지 않았어요. 빌더에서 에이전트를 연결한 뒤 다시 배포해 주세요.';

export function HostedApp({ config }: { config: PublishedBotConfig }) {
  const trimmedAgent = config.agentId.trim();
  const lb = React.useMemo(
    () => ({
      apiBase: config.apiBase.trim() || DEFAULT_CONFIG.apiBase,
      password: DEFAULT_CONFIG.password,
    }),
    [config.apiBase],
  );
  const isReal = trimmedAgent.length > 0 && lb.apiBase.length > 0;

  const [io, setIo] = React.useState<AgentIo | null>(null);
  const [ioReady, setIoReady] = React.useState(!isReal);

  React.useEffect(() => {
    if (!isReal) return;
    let alive = true;
    getAgentIo(lb, trimmedAgent)
      .then((v) => {
        if (alive) {
          setIo(v);
          setIoReady(true);
        }
      })
      .catch(() => {
        if (alive) setIoReady(true);
      });
    return () => {
      alive = false;
    };
  }, [isReal, trimmedAgent, lb]);

  const client = React.useMemo<ChatClient>(() => {
    if (isReal && ioReady) return makeAguiClient(lb, trimmedAgent, io);
    return new MockChatClient({ delayMs: 40, respond: () => ({ reply: MOCK_REPLY }) });
  }, [isReal, ioReady, lb, trimmedAgent, io]);

  const chat = useChat({ client });
  const [dark, setDark] = React.useState(config.theme.mode === 'dark');
  const themeAttrs = {
    ...config.resolved.dataAttrs,
    'data-mode': dark ? ('dark' as const) : undefined,
  };

  return (
    <div {...themeAttrs} style={config.resolved.inlineVars as React.CSSProperties} className="h-dvh bg-app">
      <WebappSurface
        title={config.agentName || config.name || 'LangBridge'}
        badge={
          <span className="rounded-pill bg-muted px-2 py-0.5 text-2xs text-fg-muted">
            {config.slug}.bots.laivdata.com
          </span>
        }
        headerRight={<ThemeToggle dark={dark} onToggle={() => setDark((d) => !d)} />}
        sessionGroups={[]}
        onNewChat={chat.reset}
        messages={chat.messages}
        phase={chat.phase}
        activity={chat.activity}
        interrupt={chat.interrupt}
        onRespondInterrupt={chat.respondInterrupt}
        error={chat.error}
        onSend={chat.send}
        attach={attachCapability(io)}
        onRetry={chat.retry}
        onAbort={chat.abort}
        renderA2ui={(messages) => (
          <A2uiPanel
            messages={messages}
            mode={dark ? 'dark' : 'light'}
            onAction={(a) => chat.sendAction(a as unknown as Record<string, unknown>)}
          />
        )}
      />
    </div>
  );
}

function ThemeToggle({ dark, onToggle }: { dark: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={dark ? '라이트 모드로 전환' : '다크 모드로 전환'}
      title={dark ? '라이트 모드로 전환' : '다크 모드로 전환'}
      className="flex size-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus-ring)]"
    >
      {dark ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}
